/**
 * Danh sách lịch hẹn — "tôi không thể đi tìm trên lịch được, lẫn mỏi cả tay sẽ không thấy"
 * (chủ dự án 21/09/2026).
 *
 * Lịch theo cột trả lời "ai rảnh lúc nào"; danh sách trả lời "chị Lan đặt hôm nào, mấy giờ, đã
 * tới chưa". Hai màn NỐI với nhau: mỗi dòng mở đúng tờ chi tiết của màn lịch (cùng thao tác: khách
 * đã tới, đổi giờ, huỷ…) và có "Xem trên lịch" để nhảy sang đúng ngày.
 *
 * Một hành động chính trên mỗi dòng (bước kế tiếp của lịch, như màn lịch). Tìm theo tên / số điện
 * thoại / mã lịch; lọc khoảng ngày, trạng thái (có đếm), kỹ thuật viên.
 */
import {
  CalendarDays,
  CalendarSearch,
  ChevronLeft,
  ChevronRight,
  Search,
  SlidersHorizontal,
  X,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  type Api,
  ApiFailure,
  buocKe,
  type DanhSachLichHen,
  type DayAppointment,
  gioPhut,
  hienSdt,
  type LichHenDong,
  ngayVn,
  type ToiLaAi,
  tenDichVu,
  tenKhach,
  themNgay,
  tienVnd,
} from './api.ts'
import { hanToastMs, kieuToast, type ToastQuay, toastSauDoi } from './bao-khach.ts'
import { DongTenTiem, useChiNhanh } from './chi-nhanh-dang-dung.tsx'
import { ChiTietLich } from './DayBoard.tsx'
import { ToDoiGio } from './DoiGio.tsx'
import { ToGoiViKhach, ToHoanTat } from './GoiVi.tsx'
import { chuNhanTre } from './man-dau.ts'
import { nhanNgayDayDu, nhanNgayNgan, phutBayGio } from './ngay.ts'
import { G } from './text/goi-vi.ts'
import { hanhDongCua, T, TRANG_THAI } from './text/vi.ts'
import {
  Avatar,
  DauTrangThaiLich,
  moBangChonNgay,
  Rong,
  ThongBao,
  useLanTaiMoiNhat,
  Xuong,
} from './ui.tsx'

type Khoang = 'hom-nay' | 'ngay-mai' | '7-toi' | '30-toi' | '7-qua' | 'mot-ngay'

/** KH-GĐ2 4.4.1: ba bộ lọc hay dùng nhất hiện sẵn; còn lại gom vào nút "Thêm". */
const CHIP_TRANG_THAI = [
  { ma: '', ten: T.statusAll },
  { ma: 'booked', ten: TRANG_THAI.booked },
  { ma: 'no_show', ten: TRANG_THAI.no_show },
] as const
const CHIP_THEM = [
  // Nhãn ngắn: "Khách đã tới" bẻ dòng trong chip nửa màn điện thoại.
  { ma: 'checked_in', ten: T.statusArrivedShort },
  { ma: 'in_service', ten: TRANG_THAI.in_service },
  { ma: 'done', ten: TRANG_THAI.done },
  { ma: 'cancelled', ten: TRANG_THAI.cancelled },
] as const

function khoangNgay(k: Khoang, motNgay: string): { tu: string; den: string } {
  const h = ngayVn()
  switch (k) {
    case 'hom-nay':
      return { tu: h, den: h }
    case 'ngay-mai':
      return { tu: themNgay(h, 1), den: themNgay(h, 1) }
    case '7-toi':
      return { tu: h, den: themNgay(h, 6) }
    case '30-toi':
      return { tu: h, den: themNgay(h, 29) }
    case '7-qua':
      return { tu: themNgay(h, -7), den: themNgay(h, -1) }
    case 'mot-ngay':
      return { tu: motNgay, den: motNgay }
  }
}

/** Dòng danh sách → đúng kiểu dữ liệu tờ chi tiết của màn lịch dùng (một tờ cho cả hai màn). */
export function thanhLichNgay(d: LichHenDong): DayAppointment {
  return {
    appointmentId: d.appointmentId,
    itemId: d.itemId ?? d.appointmentId,
    code: d.code,
    status: d.status,
    staffId: d.staffId,
    startMin: d.startMin,
    endMin: d.endMin,
    customerName: d.customerName,
    customerPhone: d.customerPhone,
    serviceName: d.serviceName,
    priceVnd: d.priceVnd,
    serviceVariantId: d.serviceVariantId ?? '',
    doiDuoc: d.doiDuoc,
    customerId: d.customerId,
    // Thiếu dòng này thì lịch "Đã xong" chưa thu mở từ Danh sách mất nút "Thu tiền" (ui-critic v11).
    ...(d.daThu === undefined ? {} : { daThu: d.daThu }),
    // N2: thiếu thì mục Tiền cọc không hiện — cọc của lịch đã huỷ / không đến không còn chỗ hoàn.
    ...(d.cocVnd === undefined ? {} : { cocVnd: d.cocVnd }),
    // Số cọc chốt: thiếu thì lịch đã huỷ mở từ Danh sách không hiện nút "Ghi nhận tiền cọc" (`daHuyTungCoc` luôn sai — K2 vòng 3).
    ...(d.cocCanVnd === undefined ? {} : { cocCanVnd: d.cocCanVnd }),
    ...(d.daXacNhan === undefined ? {} : { daXacNhan: d.daXacNhan }),
    ...(d.chuaXacNhan === undefined ? {} : { chuaXacNhan: d.chuaXacNhan }),
    ...(d.soLanDoiGio === undefined ? {} : { soLanDoiGio: d.soLanDoiGio }),
    ...(d.doiGioTu === undefined ? {} : { doiGioTu: d.doiGioTu }),
  }
}

export function TrangDanhSach({
  toi,
  api,
  onXemTrenLich,
  onKhaiTaiKhoan,
}: {
  toi: ToiLaAi
  api: Api
  onXemTrenLich: (ngay: string) => void
  /** Chỉ CHỦ tiệm có: nút "Khai tài khoản nhận tiền" ở tờ thu khi tiệm chưa có mã QR (v0.23.2). */
  onKhaiTaiKhoan?: () => void
}) {
  const choDatLich = toi.quyen.includes('dat-lich')
  const thuTien = toi.quyen.includes('thu-tien')
  const [hoanTatCho, setHoanTatCho] = useState<LichHenDong | null>(null)
  const [goiViCho, setGoiViCho] = useState<string | null>(null)
  const xemSdt = toi.quyen.includes('xem.sdt-khach')
  /** ADR-101: chi nhánh đang dùng chung cho cả máy (không còn tự gọi `branches` / lấy chi nhánh đầu). */
  const { ds: branches, id: branchId } = useChiNhanh()
  const [khoang, setKhoang] = useState<Khoang>('7-toi')
  const [motNgay, setMotNgay] = useState(ngayVn())
  const [trangThai, setTrangThai] = useState('')
  /** "Thêm": hiện các trạng thái ít dùng (khách đã tới, đang làm, đã xong, đã huỷ). */
  const [moThem, setMoThem] = useState(false)
  /** Điện thoại hẹp: ô chọn Kỹ thuật viên gấp sau chip "Lọc" (vòng 42; chi nhánh nay ở dòng tên tiệm — ADR-101). */
  const [moLoc, setMoLoc] = useState(false)
  const [ktv, setKtv] = useState('')
  const [go, setGo] = useState('')
  const [tuKhoa, setTuKhoa] = useState('')
  const [data, setData] = useState<DanhSachLichHen | null>(null)
  const [dong, setDong] = useState<LichHenDong[]>([])
  const [trang, setTrang] = useState(1)
  const [loi, setLoi] = useState<string | null>(null)
  const [chon, setChon] = useState<LichHenDong | null>(null)
  const [doiGio, setDoiGio] = useState<{
    lich: LichHenDong
    nhanVien: { id: string; ten: string }[]
  } | null>(null)
  const [bao, setBao] = useState<ToastQuay | null>(null)
  /** Tiêu đề ngày dính ngay dưới đầu trang khi cuộn — đầu trang cao bao nhiêu thì đo, không đoán. */
  const dauRef = useRef<HTMLElement>(null)
  const [caoDau, setCaoDau] = useState(0)
  useEffect(() => {
    const el = dauRef.current
    if (el === null || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => setCaoDau(el.getBoundingClientRect().height))
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  /*
   * Hàng chip khoảng ngày cuộn ngang: khoảng đang chọn (mặc định "7 ngày tới" — chip thứ 4) phải LUÔN thấy đủ chữ,
   * kẻo không biết đang lọc ngày nào (vòng 44). Tự cuộn riêng khung chip — không `scrollIntoView` (nó cuộn cả trang).
   * Chỉ chạy khi đổi khoảng / khi mở màn; người dùng tự cuộn tiếp thì không bị kéo lại.
   */
  const khoangRef = useRef<HTMLDivElement>(null)
  // biome-ignore lint/correctness/useExhaustiveDependencies: `khoang` / `motNgay` là TÍN HIỆU chạy lại (chip đang chọn đổi).
  useEffect(() => {
    const khung = khoangRef.current
    if (khung === null) return
    const chip = khung.querySelector<HTMLElement>('.chip.dang-chon')
    if (chip === null) return
    const k = khung.getBoundingClientRect()
    const c = chip.getBoundingClientRect()
    const le = 20 // chừa một mẩu chip trước để biết còn chip bên trái
    if (c.left < k.left + le || c.right > k.right - 8) khung.scrollLeft += c.left - k.left - le
  }, [khoang, motNgay])

  // Gõ tìm: đợi 300ms sau phím cuối mới hỏi máy chủ — không gọi mỗi lần gõ một chữ.
  useEffect(() => {
    const t = setTimeout(() => setTuKhoa(go.trim()), 300)
    return () => clearTimeout(t)
  }, [go])

  const { tu, den } = khoangNgay(khoang, motNgay)

  const lanMoi = useLanTaiMoiNhat()
  const nap = useCallback(
    async (trangMoi: number) => {
      if (branchId === '') return
      setLoi(null)
      const conMoi = lanMoi()
      try {
        const kq = await api.lichHen({
          branchId,
          tu,
          den,
          ...(trangThai === '' ? {} : { trangThai: [trangThai] }),
          ...(ktv === '' ? {} : { ktv }),
          ...(tuKhoa === '' ? {} : { q: tuKhoa }),
          trang: trangMoi,
        })
        if (!conMoi()) return
        setData(kq)
        setDong((cu) => (trangMoi === 1 ? kq.lichHen : [...cu, ...kq.lichHen]))
        setTrang(trangMoi)
      } catch (e) {
        if (conMoi()) setLoi(e instanceof ApiFailure ? e.messageVi : T.serverFailed)
      }
    },
    [api, branchId, tu, den, trangThai, ktv, tuKhoa, lanMoi],
  )

  useEffect(() => {
    setData(null)
    void nap(1)
  }, [nap])

  useEffect(() => {
    if (bao === null) return
    // 6 giây (K1a); việc chủ tiệm còn phải làm tiếp (gọi báo khách) giữ 10 giây.
    const t = setTimeout(() => setBao(null), hanToastMs(bao.baoKhach, 6000))
    return () => clearTimeout(t)
  }, [bao])

  /**
   * Kỹ thuật viên đã gặp trong các lần tải — đủ để lọc mà không cần quyền xem danh sách nhân viên.
   * GỘP DẦN chứ không lấy từ kết quả đang lọc: chọn Tú xong vẫn phải thấy Hà để đổi (vòng 7).
   */
  const [ktvDaThay, setKtvDaThay] = useState<Record<string, string>>({})
  useEffect(() => {
    setKtvDaThay((cu) => {
      const moi = { ...cu }
      for (const d of dong)
        if (d.staffId !== null && d.staffName !== null) moi[d.staffId] = d.staffName
      return moi
    })
  }, [dong])
  // biome-ignore lint/correctness/useExhaustiveDependencies: đổi chi nhánh là tín hiệu làm lại danh sách KTV.
  useEffect(() => {
    setKtvDaThay({})
    setKtv('')
    // Đổi chi nhánh (hiếm: chi nhánh đang dùng vừa bị ẩn): đóng mọi tờ để không ghi nhầm sang chi nhánh khác.
    setChon(null)
    setDoiGio(null)
    setHoanTatCho(null)
    setGoiViCho(null)
    setBao(null)
  }, [branchId])
  const cacKtv = useMemo(
    () => Object.entries(ktvDaThay).sort((a, b) => a[1].localeCompare(b[1], 'vi')),
    [ktvDaThay],
  )

  const theoNgay = useMemo(() => {
    const nhom = new Map<string, LichHenDong[]>()
    for (const d of dong) nhom.set(d.date, [...(nhom.get(d.date) ?? []), d])
    return [...nhom.entries()]
  }, [dong])

  // `con_coc` không phải một trạng thái (lịch đếm hai lần nếu cộng vào "Tất cả").
  const { con_coc: soConCoc = 0, ...demTrangThai } = data?.theoTrangThai ?? {}
  const tongDem = Object.values(demTrangThai).reduce((s, n) => s + n, 0)
  const coLoc = trangThai !== '' || ktv !== '' || tuKhoa !== ''
  /** Ô chọn Kỹ thuật viên gấp sau chip "Lọc" ở điện thoại hẹp (chi nhánh không còn là bộ lọc — ADR-101, nút riêng luôn thấy). */
  const coOChonPhu = choDatLich && cacKtv.length > 1
  /** Số ô chọn đang lệch mặc định (đã chọn một người) — hiện trên chip "Lọc" khi đang gấp. */
  const demOChonPhu = ktv !== '' ? 1 : 0

  async function lamBuocKe(d: LichHenDong, to: string) {
    // "Xong" của vai thu tiền đi qua tờ hoàn tất (trừ gói / ví), như ở màn lịch.
    if (to === 'done' && thuTien) {
      setHoanTatCho(d)
      return
    }
    try {
      await buocKe(api, d.appointmentId, to)
      await nap(1)
    } catch (e) {
      setLoi(e instanceof ApiFailure ? e.messageVi : T.statusFailed)
    }
  }

  async function moDoiGio(d: LichHenDong) {
    setChon(null)
    try {
      const b = await api.day(branchId, d.date)
      setDoiGio({ lich: d, nhanVien: b.staff.map((x) => ({ id: x.staffId, ten: x.fullName })) })
    } catch (e) {
      setLoi(e instanceof ApiFailure ? e.messageVi : T.serverFailed)
    }
  }

  const khoangChip: { ma: Khoang; ten: string }[] = [
    { ma: 'hom-nay', ten: T.rangeToday },
    { ma: 'ngay-mai', ten: T.rangeTomorrow },
    { ma: '7-toi', ten: T.rangeNext7 },
    { ma: '30-toi', ten: T.rangeNext30 },
    { ma: '7-qua', ten: T.rangePast7 },
  ]

  return (
    <>
      <header className="dau-man" ref={dauRef}>
        <div className="ten-man">
          <DongTenTiem tenTiem={toi.tenTiem} />
          <h1>{T.pageList}</h1>
        </div>
        <div className="o-nhap-icon o-tim">
          <Search size={20} />
          <input
            className="o-nhap"
            type="search"
            value={go}
            placeholder={xemSdt ? T.searchPlaceholder : T.searchNoPhone}
            aria-label={T.searchPlaceholder}
            onChange={(e) => setGo(e.target.value)}
          />
          {go !== '' && (
            <button
              type="button"
              className="xoa-tim"
              aria-label={T.close}
              onClick={() => setGo('')}
            >
              <X size={18} />
            </button>
          )}
        </div>
      </header>

      <main className="than-man" style={{ '--cao-dau-man': `${caoDau}px` } as React.CSSProperties}>
        <section className="the bo-loc">
          <div className="hang-loc-1">
            <div className="hang-chip cuon chip-khoang" ref={khoangRef}>
              <label className={`chip chip-ngay ${khoang === 'mot-ngay' ? 'dang-chon' : ''}`}>
                <CalendarDays size={16} />
                {khoang === 'mot-ngay' ? nhanNgayDayDu(motNgay) : T.rangePickDay}
                <input
                  type="date"
                  aria-label={T.rangePickDay}
                  value={motNgay}
                  onClick={(e) => {
                    // Bấm chip là vào chế độ "một ngày" ngay (chọn lại đúng ngày đang hiện thì trình duyệt
                    // không bắn `change`, chip sẽ không đổi nếu chỉ chờ sự kiện đó).
                    setKhoang('mot-ngay')
                    moBangChonNgay(e.currentTarget)
                  }}
                  onChange={(e) => {
                    if (e.target.value === '') return
                    setMotNgay(e.target.value)
                    setKhoang('mot-ngay')
                  }}
                />
              </label>
              {khoangChip.map((c) => (
                <button
                  key={c.ma}
                  type="button"
                  className={`chip ${khoang === c.ma ? 'dang-chon' : ''}`}
                  aria-pressed={khoang === c.ma}
                  onClick={() => setKhoang(c.ma)}
                >
                  {c.ten}
                </button>
              ))}
            </div>
            {/* Điện thoại hẹp (≤ 479px): ô chọn Kỹ thuật viên gấp sau chip này — khối lọc từng cao 260px, lịch đầu bắt đầu ở y = 490. */}
            {coOChonPhu && (
              <button
                type="button"
                className={`chip chip-loc ${demOChonPhu > 0 ? 'dang-chon' : ''}`}
                aria-expanded={moLoc}
                onClick={() => setMoLoc((x) => !x)}
              >
                <SlidersHorizontal size={16} />
                {T.filterBtn}
                {demOChonPhu > 0 && <span className="so-dem">{demOChonPhu}</span>}
              </button>
            )}
          </div>
          {/* Hàng riêng: đặt chung hàng chip khoảng ngày thì che mất "Chọn ngày" (vòng 8). */}
          <div className={`chon-phu ${moLoc ? 'mo' : ''}`}>
            {/* Kỹ thuật viên chỉ thấy lịch của mình (máy chủ lọc) — lọc theo người là thừa. */}
            {choDatLich && cacKtv.length > 1 && (
              <select
                className="o-chon gon"
                aria-label={T.allStaff}
                value={ktv}
                onChange={(e) => setKtv(e.target.value)}
              >
                <option value="">{T.allStaff}</option>
                {cacKtv.map(([id, ten]) => (
                  <option key={id} value={id}>
                    {ten}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div className="hang-chip chip-trang-thai">
            {[
              ...CHIP_TRANG_THAI,
              // Mở "Thêm" thì hiện đủ; đang lọc theo một trạng thái trong "Thêm" thì chip đó vẫn hiện (biết mình
              // đang xem gì).
              ...CHIP_THEM.filter((c) => moThem || trangThai === c.ma),
              // Chờ duyệt / chờ cọc: chỉ hiện khi có — tổng "Tất cả" phải cộng đủ từ các chip (ui-critic v21).
              ...(['pending', 'awaiting_deposit'] as const)
                .filter((ma) => (demTrangThai[ma] ?? 0) > 0 || trangThai === ma)
                .map((ma) => ({ ma, ten: TRANG_THAI[ma] ?? ma })),
              // N2: chỉ hiện khi có lịch còn giữ cọc — chỗ quầy hoàn / giữ cọc của lịch đã huỷ, không
              // đến, hết giờ giữ (những lịch này không còn trên màn lịch ngày).
              ...(soConCoc > 0 || trangThai === 'con_coc'
                ? [{ ma: 'con_coc', ten: T.depositHeldChip }]
                : []),
            ].map((c) => {
              const so = c.ma === '' ? tongDem : (data?.theoTrangThai[c.ma] ?? 0)
              return (
                <button
                  key={c.ma}
                  type="button"
                  className={`chip ${trangThai === c.ma ? 'dang-chon' : ''} ${c.ma === '' ? '' : `tt-${c.ma}`} ${c.ma === 'con_coc' ? 'chip-rieng' : ''}`}
                  aria-pressed={trangThai === c.ma}
                  onClick={() => setTrangThai(c.ma)}
                >
                  {/* "Đang giữ cọc" là bộ lọc ngang mọi trạng thái, không phải một trạng thái → không chấm màu. */}
                  {c.ma !== '' && c.ma !== 'con_coc' && <i className="cham-tt" />}
                  {c.ten}
                  {/* Đang tải thì không hiện "0" — nhìn như "không có lịch" (vòng 7). */}
                  {data !== null && <span className="so-dem">{so}</span>}
                </button>
              )
            })}
            <button
              type="button"
              className="chip"
              aria-expanded={moThem}
              onClick={() => setMoThem((x) => !x)}
            >
              {moThem ? T.statusLess : T.statusMore}
              {moThem ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
            </button>
          </div>
        </section>

        {loi !== null && (
          <ThongBao loai="loi" onDong={() => setLoi(null)}>
            {loi}
          </ThongBao>
        )}

        {branches !== null && branches.length === 0 ? (
          <div className="the">
            <Rong
              icon={<CalendarDays size={28} />}
              tieuDe={T.notReadyTitle}
              moTa={T.notReadyHint}
            />
          </div>
        ) : data === null ? (
          <div className="the the-dem" style={{ display: 'grid', gap: 12 }}>
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <Xuong key={i} cao={56} />
            ))}
          </div>
        ) : dong.length === 0 ? (
          <div className="the">
            <Rong
              icon={<CalendarSearch size={28} />}
              tieuDe={T.listEmptyTitle}
              moTa={T.listEmptyHint}
              hanhDong={
                coLoc ? (
                  <button
                    type="button"
                    className="nut nut-vien"
                    onClick={() => {
                      setTrangThai('')
                      setKtv('')
                      setGo('')
                    }}
                  >
                    {go !== '' ? T.clearSearchAndFilters : T.clearFilters}
                  </button>
                ) : undefined
              }
            />
          </div>
        ) : (
          <>
            {theoNgay.map(([ngay, cac]) => (
              <section key={ngay} className="the nhom-ngay">
                <h2 className="dau-nhom-ngay">
                  <span className="ngay-dai">{T.dayGroup(nhanNgayDayDu(ngay), cac.length)}</span>
                  <span className="ngay-ngan">
                    <span className="nhom-nhan">{nhanNgayNgan(ngay)}</span>
                    <span className="nhom-dau"> · </span>
                    <span className="nhom-so">{T.dayCount(cac.length)}</span>
                  </span>
                  <button
                    type="button"
                    className="nut nut-nhat nut-nho"
                    onClick={() => onXemTrenLich(ngay)}
                  >
                    <span className="ngay-dai">{T.viewOnCalendar}</span>
                    <span className="ngay-ngan">{T.viewOnCalendarShort}</span>
                    <ChevronRight size={16} />
                  </button>
                </h2>
                <ul className="ds-hen">
                  {cac.map((d) => (
                    <DongHen
                      key={d.appointmentId}
                      d={d}
                      choDatLich={choDatLich}
                      thuTien={thuTien}
                      onMo={() => setChon(d)}
                      onThu={() => setHoanTatCho(d)}
                      onBuocKe={(to) => void lamBuocKe(d, to)}
                    />
                  ))}
                </ul>
              </section>
            ))}
            {data.conNua && (
              <button
                type="button"
                className="nut nut-vien nut-rong"
                onClick={() => void nap(trang + 1)}
              >
                {T.loadMore}
              </button>
            )}
          </>
        )}
      </main>

      {chon !== null && (
        <ChiTietLich
          choQuay={choDatLich}
          lich={thanhLichNgay(chon)}
          ngay={chon.date}
          tenKtv={chon.staffName ?? T.unassigned}
          api={api}
          onDong={() => setChon(null)}
          onDaToi={() => undefined}
          onDaDoi={(thongBao) => {
            setChon(null)
            if (thongBao !== undefined) setBao(thongBao)
            void nap(1)
          }}
          onDoiGio={choDatLich && chon.doiDuoc ? () => void moDoiGio(chon) : null}
          onXemTrenLich={() => {
            const ngay = chon.date
            setChon(null)
            onXemTrenLich(ngay)
          }}
          coc={{
            choNhan: thuTien,
            choHoan: toi.quyen.includes('hoan-dao-tien'),
            soTienMacDinh: null,
          }}
          hoSo={{ coAnh: toi.quyen.includes('xem.anh-khach') }}
          onHoanTat={
            thuTien
              ? () => {
                  setHoanTatCho(chon)
                  setChon(null)
                }
              : undefined
          }
          onGoiVi={
            thuTien && chon.customerId !== null
              ? () => {
                  setGoiViCho(chon.customerId)
                  setChon(null)
                }
              : undefined
          }
        />
      )}

      {hoanTatCho !== null && (
        <ToHoanTat
          api={api}
          appointmentId={hoanTatCho.appointmentId}
          tenKhachHang={tenKhach(hoanTatCho.customerName)}
          onDong={() => setHoanTatCho(null)}
          onLoi={setLoi}
          onXong={(thongBao) => {
            setHoanTatCho(null)
            setBao({ chu: thongBao })
            void nap(1)
          }}
          {...(onKhaiTaiKhoan === undefined ? {} : { onKhaiTaiKhoan })}
        />
      )}

      {goiViCho !== null && branchId !== '' && (
        <ToGoiViKhach
          key={goiViCho}
          api={api}
          toi={toi}
          customerId={goiViCho}
          branchId={branchId}
          onDong={() => setGoiViCho(null)}
          onMoKhac={setGoiViCho}
          onDaGop={() => void nap(1)}
        />
      )}

      {doiGio !== null && branchId !== '' && (
        <ToDoiGio
          lich={thanhLichNgay(doiGio.lich)}
          api={api}
          branchId={branchId}
          date={doiGio.lich.date}
          nhanVien={doiGio.nhanVien}
          onDong={() => setDoiGio(null)}
          onXong={async (staffId, startMin, ngay) => {
            const d = doiGio.lich
            setDoiGio(null)
            try {
              const kq = await api.doiGio(d.appointmentId, { date: ngay, startMin, staffId })
              setBao({
                chu: toastSauDoi(tenKhach(d.customerName), kq.baoKhach),
                baoKhach: kq.baoKhach,
              })
            } catch (e) {
              setLoi(e instanceof ApiFailure ? e.messageVi : T.moveFailed)
            } finally {
              await nap(1)
            }
          }}
        />
      )}

      {bao !== null && (
        <ThongBao loai={kieuToast(bao.baoKhach)} noi onDong={() => setBao(null)}>
          {bao.chu}
        </ThongBao>
      )}
    </>
  )
}

function DongHen({
  d,
  choDatLich,
  thuTien,
  onMo,
  onThu,
  onBuocKe,
}: {
  d: LichHenDong
  choDatLich: boolean
  thuTien: boolean
  onMo: () => void
  /** Mở thẳng tờ thu tiền (ui-critic v13: trước phải qua Chi tiết rồi bấm lần nữa). */
  onThu: () => void
  onBuocKe: (to: string) => void
}) {
  // Bước kế tiếp ("Khách đã tới", "Bắt đầu làm"…) chỉ có nghĩa với lịch HÔM NAY — lịch ngày mai
  // mà có nút "Khách đã tới" là mời bấm nhầm.
  const chinh =
    d.date === ngayVn()
      ? hanhDongCua(d.status, d.source).find((h) => h.kind === 'primary')
      : undefined
  const ten = tenKhach(d.customerName)
  // Lịch đã xong mà quầy chưa thu: chip + nút "Thu tiền" ngay trên dòng (ui-critic v12).
  const chuaThu = d.status === 'done' && d.daThu === false
  // Cùng nhãn "Trễ …" / "Chưa tới" như màn Lịch (ui-critic v45: Lịch ghi "Chưa tới", Danh sách chỉ "Đã đặt").
  const chuTre = chuNhanTre(d, d.date === ngayVn() ? phutBayGio() : null)
  return (
    <li className={`dong-hen tt-${d.status}`}>
      <button type="button" className="dong-hen-mo" onClick={onMo}>
        <span className="dh-gio">
          <b>{gioPhut(d.startMin)}</b>
          <span>{gioPhut(d.endMin)}</span>
        </span>
        <span className="dh-khach">
          <b>{ten}</b>
          <span>
            {d.customerPhone !== null && (
              <span className="khong-be">{hienSdt(d.customerPhone)}</span>
            )}
            {d.customerPhone !== null && <span className="dh-cham"> · </span>}
            <span className="dh-ma">{d.code}</span>
          </span>
        </span>
        <span className="dh-dv">
          <span className="dh-dv-ten">
            <span>{tenDichVu(d.serviceName)}</span>
            {chuaThu && <b className="nhan-chua-thu">{G.chuaThu}</b>}
            {chuTre !== null && <b className="nhan-tre">{chuTre}</b>}
          </span>
          <span className="dh-dau-lich">
            <DauTrangThaiLich
              soLanDoi={d.soLanDoiGio}
              gioCu={d.doiGioTu}
              daXacNhan={d.daXacNhan}
              chuaXacNhan={d.chuaXacNhan}
            />
          </span>
          <span className="dh-nguon">
            {d.source === 'miniapp' ? T.sourceMiniapp : T.sourceCounter}
            <span className="dh-ktv-phu"> · {d.staffName ?? T.unassigned}</span>
          </span>
        </span>
        <span className="dh-ktv">
          {d.staffName !== null && <Avatar ten={d.staffName} co="nho" />}
          <span>{d.staffName ?? T.unassigned}</span>
        </span>
        <span className="dh-gia">{d.priceVnd === null ? '' : tienVnd(d.priceVnd)}</span>
        <span className={`vien-tt tt-${d.status}`}>{TRANG_THAI[d.status] ?? d.status}</span>
      </button>
      {chuaThu && thuTien ? (
        <button type="button" className="nut nut-buoc dh-buoc" onClick={onThu}>
          {G.collectNow}
        </button>
      ) : chinh !== undefined && choDatLich ? (
        <button type="button" className="nut nut-buoc dh-buoc" onClick={() => onBuocKe(chinh.to)}>
          {chinh.label}
        </button>
      ) : (
        choDatLich && <span className="dh-buoc-trong" aria-hidden="true" />
      )}
    </li>
  )
}
